import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Paper, Typography, Menu, MenuItem } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMst } from '../../core/stores';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';

import {
  ExistingDigitalOrderOrganization,
  NewDigitalOrderOrganization,
  NewOrExisting,
  NewOrExistingFacility,
  OrderFacility,
} from '../../models';
import { OrderBreadcrumbs } from '../../components/OrderBreadcrumbs/OrderBreadcrumbs';
import { FacilityPanel } from '../../components/Facilities/FacilityPanel';
import { difference } from 'underscore';

export interface DOOrganizationProps extends RouteComponentProps<{ id: string; from: string }> {}

const useStyles = makeStyles(theme => ({
  paper: {
    display: 'flex',
    width: '88%',
    flexShrink: 0,
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  formContainer: {
    flexGrow: 1,
    height: '100%',
    margin: theme.spacing(2),
    '& > *': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  summary: {
    margin: 0,
  },
  buttonGroup: {
    width: '100%',
  },
  button: {
    width: '50%',
  },
  title: {
    width: '100%',
  },
  addButtonRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  buttomButtonRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttonContainers: {
    width: '100%',
    '& > *': {
      marginTop: theme.spacing(1),
    },
  },
}));

export const Facilities: React.FC<DOOrganizationProps> = observer(({ match, location }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const orderId = match.params.id;
  const { digitalOrdersStore, navigationStore, configStore } = useMst();
  const [selection, setSelection] = useState('');
  const currentOrder = digitalOrdersStore.order(orderId);
  const [facilities, setFacilities] = useState([]);
  const [validityList, setValidityList] = useState([]);
  const [isNewOrg, setIsNewOrg] = useState(false);

  useEffect(() => {
    const newValidityList = [];
    if (currentOrder.facility.facilities.length) {
      currentOrder.facility.facilities.forEach(() => {
        newValidityList.push(true);
      });
      setFacilities(currentOrder.facility?.facilities.toJSON() || []);
      setValidityList(newValidityList);
    }
  }, [currentOrder, currentOrder.facility, currentOrder.facility.facilities]);

  useEffect(() => {
    const order = digitalOrdersStore.order(orderId);
    const orgId = (order.organization as any).organizationId;
    if (orgId) {
      digitalOrdersStore.getOrganizationFacilities(orgId.toString());
    }
  }, [orderId, digitalOrdersStore]);

  const submit = () => {
    digitalOrdersStore.submitDigitalOrder(orderId);
    navigationStore.push(`/digital-orders/${orderId}/products`);
  };

  const back = () => {
    navigationStore.push(`/digital-orders/${orderId}/organization`);
  };

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClickMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const onChange = useCallback(
    indexToUpdate => (updatedFacility, isValid) => {
      let facilityList = facilities;

      if (indexToUpdate > -1) {
        facilityList[indexToUpdate] = updatedFacility;
      } else {
        facilityList = facilityList.concat(updatedFacility);
      }

      let payload = {
        facility: {
          facilities: facilityList,
        },
      } as any;
      if (currentOrder.organization.newOrExistingOrganization === NewOrExisting.EXISTING) {
        payload.facility.organizationId = (currentOrder.organization as ExistingDigitalOrderOrganization).organizationId;
      }
      if (currentOrder.organization.newOrExistingOrganization === NewOrExisting.NEW) {
        payload.facility.isActive = (currentOrder.organization as NewDigitalOrderOrganization).isActive;
      }

      let newList = [...validityList];
      if (indexToUpdate > -1) {
        newList[indexToUpdate] = isValid;
      } else {
        newList.push(false);
      }
      setValidityList(newList);

      if (indexToUpdate >= 0) {
        digitalOrdersStore.writeToCurrentOrder(orderId, payload);
      } else {
        setFacilities(facilityList);
      }
    },
    [currentOrder.organization, digitalOrdersStore, facilities, orderId, validityList],
  );

  const onRemove = index => () => {
    let newFacilities = [...facilities];
    let newValidities = [...validityList];
    newFacilities.splice(index, 1);
    newValidities.splice(index, 1);
    setFacilities(newFacilities);
    setValidityList(newValidities);

    let payload = {
      facility: {
        facilities: newFacilities,
      },
    } as any;

    if (currentOrder.products?.integrations?.iFIT) {
      const facilityNames = newFacilities.map(f =>
        f.facilityId ? digitalOrdersStore.facilityMap.get(f.facilityId).name : f.name,
      );
      const clone = { ...currentOrder.productDetails.iFIT };
      const keysToDelete = difference(Object.keys(clone), facilityNames);
      keysToDelete.forEach(key => delete clone[key]);
      payload.productDetails = {
        ...currentOrder.productDetails,
        iFIT: clone,
      };
    }

    digitalOrdersStore.writeToCurrentOrder(orderId, payload);
  };

  const pushNewFacility = useCallback(() => {
    let newFacName = '';
    if (currentOrder.organization.newOrExistingOrganization) {
      const orgId = (currentOrder.organization as ExistingDigitalOrderOrganization).organizationId;
      newFacName = configStore.translations[`ORGANIZATION_FACILITY_TEMPLATE_${orgId}`] || '';
    }
    onChange(-1)(
      {
        type: NewOrExistingFacility.NEW,
        city: '',
        isActive: '1', // '1' or '0'
        name: newFacName,
        postalCode: '',
        street1: '',
        street2: '',
        nickname: null,
        countryId: null,
        stateId: null,
      },
      false,
    );
  }, [onChange, configStore.translations, currentOrder.organization]);

  const pushExistingFacility = useCallback(() => {
    onChange(-1)({ facilityId: null, type: NewOrExistingFacility.EXISTING }, false);
  }, [onChange]);

  const handleCloseMenu = (typeToAdd: string | null) => () => {
    if (typeToAdd === NewOrExistingFacility.NEW) {
      pushNewFacility();
    } else if (typeToAdd === NewOrExistingFacility.EXISTING) {
      pushExistingFacility();
    }
    setAnchorEl(null);
  };

  useEffect(() => {
    if (selection === 'new') {
      pushNewFacility();
    } else if (selection === 'existing') {
      pushExistingFacility();
    }
    setSelection('');
  }, [selection, pushExistingFacility, pushNewFacility]);

  if (
    facilities.length === 0 &&
    currentOrder.organization.newOrExistingOrganization === NewOrExisting.NEW
  ) {
    setIsNewOrg(true);
    pushNewFacility();
  }

  return (
    <Paper className={classes.paper}>
      <div className={classes.formContainer}>
        <Typography className={classes.title} variant="h5">
          Select facilities
        </Typography>
        {facilities.length === 0 && (
          <div className={classes.formContainer}>
            <Typography className={classes.title} variant="body1">
              Would you like to add a new or existing facility?
            </Typography>

            <ToggleButtonGroup
              className={classes.buttonGroup}
              exclusive
              value={selection}
              onChange={(evt, newVal) => setSelection(newVal)}
            >
              <ToggleButton className={classes.button} value="new">
                New Facility
              </ToggleButton>
              <ToggleButton className={classes.button} value="existing">
                Existing Facility
              </ToggleButton>
            </ToggleButtonGroup>
            <Button variant="contained" color="secondary" onClick={back}>
              Back
            </Button>
          </div>
        )}
        {facilities.length > 0 && (
          <div className={classes.formContainer}>
            {facilities.map((f: OrderFacility, index) => {
              const key = `${f.type}-${index}`;
              return (
                <FacilityPanel
                  facility={f}
                  orderId={orderId}
                  key={key}
                  onFormChange={onChange(index)}
                  onRemove={onRemove(index)}
                />
              );
            })}

            <div className={classes.buttonContainers}>
              <div className={classes.buttomButtonRow}>
                <Button variant="contained" color="secondary" onClick={back}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={submit}
                  disabled={validityList.some(valid => !valid)}
                >
                  Next
                </Button>
              </div>
              <div className={classes.addButtonRow}>
                <Button
                  aria-controls="simple-menu"
                  aria-haspopup="true"
                  onClick={isNewOrg ? pushNewFacility : handleClickMenu}
                  variant="contained"
                  color="primary"
                >
                  Add More
                </Button>
                {!isNewOrg && (
                  <Menu
                    id="simple-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu(null)}
                  >
                    <MenuItem onClick={handleCloseMenu(NewOrExistingFacility.NEW)}>
                      New Facility
                    </MenuItem>
                    <MenuItem onClick={handleCloseMenu(NewOrExistingFacility.EXISTING)}>
                      Existing Facility
                    </MenuItem>
                  </Menu>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <OrderBreadcrumbs className={classes.summary} orderId={orderId} />
    </Paper>
  );
});
